import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';

/**
 * A single launchable walkthrough surfaced in the Ask Ryn panel's
 * "available walkthroughs" list. Shape mirrors the SS-API
 * WalkthroughDocumentLookupResultDto (camel-cased by the response transform);
 * the panel only needs id + title to render a row and launch the tour.
 */
export interface AskRynWalkthrough {
  id: number;
  title: string;
  path: string;
  reusable: boolean;
  /**
   * The route the tour starts on, used to bucket it under "On this page".
   * Populated server-side (doc path when it's a route, else the first nav step).
   * Optional until the backend ships it; matching falls back to `path`.
   */
  startPath?: string;
}

/** Strip query/fragment, trim slashes, lowercase — so two routes compare cleanly. */
function normalizeRoute(value: string | undefined | null): string {
  if (!value) return '';
  return value
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
}

/**
 * Split the app's walkthroughs into the ones that belong to the current page and
 * the rest. A tour is "on this page" when its route (`startPath`, falling back to
 * `path`) equals the current route or is an ancestor of it. A tour whose only
 * route hint is a non-route slug can't be page-matched, so it falls to "others"
 * rather than guessing — keeps the bucketing honest instead of fuzzy.
 */
export function splitWalkthroughsByRoute(
  walkthroughs: AskRynWalkthrough[],
  currentRoute: string
): { onPage: AskRynWalkthrough[]; others: AskRynWalkthrough[] } {
  const current = normalizeRoute(currentRoute);
  const onPage: AskRynWalkthrough[] = [];
  const others: AskRynWalkthrough[] = [];

  for (const w of walkthroughs) {
    const raw = w.startPath ?? w.path;
    // Only a real route (leading slash) is page-matchable; a slug is not.
    const tourRoute = raw?.startsWith('/') ? normalizeRoute(raw) : '';
    const matches =
      !!tourRoute &&
      !!current &&
      (current === tourRoute || current.startsWith(`${tourRoute}/`));
    (matches ? onPage : others).push(w);
  }

  return { onPage, others };
}

/**
 * Fetches the published-only walkthrough list for the current app so the Ask Ryn
 * panel can show users the guided tours available on the page they're on.
 *
 * Talks to the Software Security API directly (NOT the Ask Ryn bridge that
 * AskRynService uses) via a MyHttpClientFactory client, mirroring
 * HelpManagementService's SS-API setup. MyHttpClientFactory lives in
 * `@ups/xplat/core`, so this keeps the one-way dependency intact: ask-ryn never
 * imports in-app-help. The endpoint is fail-closed (published-only, no draft
 * path server-side), so drafts can never reach an end user through this list.
 */
@Injectable({ providedIn: 'root' })
export class AskRynWalkthroughService {
  private readonly http: HttpClient;

  constructor(clientFactory: MyHttpClientFactory) {
    this.http = clientFactory.createHttpClient(
      environment.urls.securityAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /**
   * Published walkthroughs for an application, by its security app GUID
   * (environment.security.appId). Returns titles to launch; the panel launches
   * one by dispatching a `walkthrough` action through AskRynUiService.
   */
  getPublishedWalkthroughs(
    applicationGuid: string
  ): Observable<AskRynWalkthrough[]> {
    return this.http.get<AskRynWalkthrough[]>(
      `api/walkthrough/byApp/${applicationGuid}`
    );
  }
}
